import {SimpleEventEmitter} from '@sail/utils';

import type {IReports} from '@sail/observability';
import type {EventCallback} from '@sail/utils';
import type {Cookies} from '@stripe-internal/stripe-cookies';

export type CookieEvent =
  | {
      type: 'remove';
      name: string;
    }
  | {
      type: 'set';
      name: string;
      value: string;
    };

export type CookieEventCallback = EventCallback<[CookieEvent]>;

export default function createCookieEventListener(
  stripeCookie: Cookies,
  reports: IReports,
): SimpleEventEmitter<[CookieEvent]> {
  const simpleEventEmitter = new SimpleEventEmitter<[CookieEvent]>();

  const descriptor =
    Object.getOwnPropertyDescriptor(document, 'cookie') ??
    Object.getOwnPropertyDescriptor(Document.prototype, 'cookie') ??
    Object.getOwnPropertyDescriptor(HTMLDocument.prototype, 'cookie');

  if (!descriptor?.configurable) {
    reports.warning(
      `Cannot patch cookie event listeners, descriptor is not configurable`,
      {
        project: 'sail_core',
      },
    );

    return simpleEventEmitter;
  }

  try {
    Object.defineProperty(document, 'cookie', {
      ...descriptor,
      set: (value) => {
        descriptor.set?.call(document, value);

        const [cookieName] = value.split('=');
        const cookieValue = stripeCookie.get(cookieName);

        simpleEventEmitter.notify(
          cookieValue == null
            ? {type: 'remove', name: cookieName}
            : {type: 'set', name: cookieName, value: cookieValue},
        );
      },
    });
  } catch (error) {
    reports.warning(`Cannot patch cookie event listeners`, {
      project: 'sail_core',
      extras: {error},
    });
  }

  return simpleEventEmitter;
}
